// SearchFilters Component
// Advanced filters for talent search

import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaTrophy, FaStar, FaCalendarAlt } from 'react-icons/fa';
import { useSports } from '../../../hooks/useSports';
import styles from './SearchFilters.module.css';

// Match backend availability status options
const AVAILABILITY_OPTIONS = [
  { value: 'actively_looking', label: 'Actively Looking' },
  { value: 'open_to_opportunities', label: 'Open to Opportunities' },
  { value: 'open_to_offers', label: 'Open to Offers' }
];

// Competition levels - maps to availability.preferredLevels in profile
// Currently not supported by backend API - reserved for future use
const LEVEL_OPTIONS = [
  'Youth Academy', 'High School', 'College/University',
  'Semi-Professional', 'Professional', 'National Team'
];

const SearchFilters = ({ filters, onFilterChange, onClearFilters, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const { sportNames, getEmoji, loading: sportsLoading } = useSports();

  // Sync localFilters with parent filters when they change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Handle filter change
  const handleChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle range change
  const handleRangeChange = (key, field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // Apply filters
  const handleApply = () => {
    // Ensure default values are included
    const filtersToApply = {
      ...localFilters,
      // Ensure ageRange has default values if not set
      ageRange: localFilters.ageRange || { min: 0, max: 90 },
    };
    onFilterChange(filtersToApply);
    onClose();
  };

  // Clear all filters - reset to defaults matching profile structure
  const handleClear = () => {
    const emptyFilters = {
      sport: '',              // athleticProfile.primarySport
      position: '',           // athleticProfile.positions[].position
      location: '',           // location.city/state/country
      ageRange: { min: 0, max: 90 },  // calculated from dateOfBirth
      availability: [],       // availability.status
      minSkillLevel: null     // average of skills.*.level
    };
    setLocalFilters(emptyFilters);
    onClearFilters();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.filtersPanel} onClick={(e) => e.stopPropagation()}>
      <div className={styles.filtersHeader}>
        <h2 className={styles.filtersTitle}>Filters</h2>
        <button 
          className={styles.closeButton}
          onClick={onClose}
        >
          <FaTimes />
        </button>
      </div>

      <div className={styles.filtersContent}>
        {/* Location Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            <FaMapMarkerAlt /> Location
          </label>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="City, State, or Country"
            value={localFilters.location || ''}
            onChange={(e) => handleChange('location', e.target.value)}
          />
          <small className={styles.helpText}>
            Enter city, state, or country (e.g., "New York", "California", "USA")
          </small>
        </div>

        {/* Sport Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Sport</label>
          <select
            className={styles.filterSelect}
            value={localFilters.sport || ''}
            onChange={(e) => handleChange('sport', e.target.value)}
          >
            <option value="">Any Sport</option>
            {sportNames.map(sport => (
              <option key={sport} value={sport}>{getEmoji(sport)} {sport}</option>
            ))}
          </select>
        </div>

        {/* Position Filter */}
        {localFilters.sport && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Position</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Enter position"
              value={localFilters.position || ''}
              onChange={(e) => handleChange('position', e.target.value)}
            />
          </div>
        )}

        {/* Age Range - Calculated from profile.dateOfBirth */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            <FaCalendarAlt /> Age Range
          </label>
          <div className={styles.rangeInputs}>
            <input
              type="number"
              className={styles.rangeInput}
              placeholder="0"
              value={localFilters.ageRange?.min ?? ''}
              onChange={(e) => handleRangeChange('ageRange', 'min', e.target.value ? parseInt(e.target.value) : 0)}
              min="0"
              max="90"
            />
            <span>to</span>
            <input
              type="number"
              className={styles.rangeInput}
              placeholder="90"
              value={localFilters.ageRange?.max ?? ''}
              onChange={(e) => handleRangeChange('ageRange', 'max', e.target.value ? parseInt(e.target.value) : 90)}
              min="0"
              max="90"
            />
          </div>
        </div>

        {/* Competition Level - Maps to availability.preferredLevels in profile */}
        {/* Note: Backend currently doesn't support this filter - will be added in future */}
        {/* Commenting out until backend support is added
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Competition Level</label>
          <div className={styles.checkboxGroup}>
            {LEVEL_OPTIONS.map(level => (
              <label key={level} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={localFilters.levels?.includes(level) || false}
                  onChange={(e) => {
                    const current = localFilters.levels || [];
                    if (e.target.checked) {
                      handleChange('levels', [...current, level]);
                    } else {
                      handleChange('levels', current.filter(l => l !== level));
                    }
                  }}
                />
                <span className={styles.checkmark}></span>
                {level}
              </label>
            ))}
          </div>
        </div>
        */}

        {/* Availability Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Availability Status</label>
          <div className={styles.checkboxGroup}>
            {AVAILABILITY_OPTIONS.map(option => (
              <label key={option.value} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={localFilters.availability?.includes(option.value) || false}
                  onChange={(e) => {
                    const current = localFilters.availability || [];
                    if (e.target.checked) {
                      handleChange('availability', [...current, option.value]);
                    } else {
                      handleChange('availability', current.filter(a => a !== option.value));
                    }
                  }}
                />
                <span className={styles.checkmark}></span>
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {/* Minimum Skill Level Filter */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            <FaStar /> Minimum Skill Level
          </label>
          <input
            type="range"
            className={styles.rangeSlider}
            min="0"
            max="10"
            step="1"
            value={localFilters.minSkillLevel || 0}
            onChange={(e) => handleChange('minSkillLevel', parseInt(e.target.value))}
          />
          <div className={styles.rangeValue}>
            Level {localFilters.minSkillLevel || 0} and above (1-10 scale)
          </div>
        </div>

        {/* Note: Awards and Media filters not yet supported by backend */}
        {/* Will be added in future update */}
      </div>

      <div className={styles.filtersFooter}>
        <button 
          className={styles.clearButton}
          onClick={handleClear}
        >
          Clear All
        </button>
        <button 
          className={styles.applyButton}
          onClick={handleApply}
        >
          Apply Filters
        </button>
      </div>
    </div>
    </div>
  );
};

export default SearchFilters;
